import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getConfig } from "../config";
import { apiErrorDetail } from "../http";
import {
  getExpensesYear,
  createExpense,
  updateExpense,
  removeExpense,
  createExpenseGroup,
  updateExpenseGroup,
  removeExpenseGroup,
  createRecurringExpense,
  updateRecurringExpense,
  removeRecurringExpense,
} from "../api/expenses";

/** A year of expenses; the old year stays visible while the next loads. */
export function useExpensesYear(year) {
  return useQuery({
    queryKey: ["expenses", year],
    queryFn: () => getExpensesYear(year),
    placeholderData: keepPreviousData,
  });
}

// Every write invalidates every year: a group or rule shows in several.
function useInvalidateExpenses() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["expenses"] });
}

const fail = (fallback) => (error) => getConfig().notify.error(apiErrorDetail(error) || fallback);

/** Create (no id) or update (with id) an expense. Rejects on failure so a
 *  dialog can stay open with the user's input. */
export function useSaveExpense() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ payload, id }) => (id ? updateExpense(id, payload) : createExpense(payload)),
    onSuccess: async (_data, { id }) => {
      getConfig().notify.success(`Expense ${id ? "updated" : "added"}`);
      await invalidate();
    },
    onError: fail("Could not save the expense"),
  });
}

export function useDeleteExpense() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (id) => removeExpense(id),
    onSuccess: async () => {
      getConfig().notify.success("Expense deleted");
      await invalidate();
    },
    onError: fail("Could not delete the expense"),
  });
}

export function useSaveExpenseGroup() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ payload, id }) => (id ? updateExpenseGroup(id, payload) : createExpenseGroup(payload)),
    onSuccess: async (_data, { id }) => {
      getConfig().notify.success(`Group ${id ? "updated" : "added"}`);
      await invalidate();
    },
    onError: fail("Could not save the group"),
  });
}

/** Deletes a group; its entries stay as normal expenses. */
export function useDeleteExpenseGroup() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (id) => removeExpenseGroup(id),
    onSuccess: async () => {
      getConfig().notify.success("Group deleted");
      await invalidate();
    },
    onError: fail("Could not delete the group"),
  });
}

export function useSaveRecurringExpense() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ payload, id }) => (id ? updateRecurringExpense(id, payload) : createRecurringExpense(payload)),
    onSuccess: async (_data, { id }) => {
      getConfig().notify.success(`Recurring expense ${id ? "updated" : "added"}`);
      await invalidate();
    },
    onError: fail("Could not save the recurring expense"),
  });
}

export function useDeleteRecurringExpense() {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (id) => removeRecurringExpense(id),
    onSuccess: async () => {
      getConfig().notify.success("Recurring expense deleted");
      await invalidate();
    },
    onError: fail("Could not delete the recurring expense"),
  });
}
